import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Eye, Edit2, MoreVertical, CheckCircle,
    XCircle, AlertTriangle, FileText, Building2, Calendar
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge, Tabs,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    ConfirmDialog, toast
} from '../../components/common';
import { contractApi } from '../../services/api';
import './Contracts.css';

export default function ContractList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [activeTab, setActiveTab] = useState('all');
    const [actionContract, setActionContract] = useState(null);
    const [actionType, setActionType] = useState('');

    const { data, isLoading } = useQuery({
        queryKey: ['contracts', activeTab, page, pageSize, search, statusFilter],
        queryFn: () => activeTab === 'expiring'
            ? contractApi.getExpiring()
            : contractApi.list({ page, limit: pageSize, search, status: statusFilter }),
        select: (res) => res.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ id, action }) => {
            switch (action) {
                case 'approve': return contractApi.approve(id);
                case 'activate': return contractApi.activate(id);
                case 'terminate': return contractApi.terminate(id, { reason: 'Terminated by admin' });
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`Contract ${action}d successfully`);
            queryClient.invalidateQueries(['contracts']);
            setActionContract(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'pending_approval': 'warning',
            'approved': 'info',
            'active': 'success',
            'expired': 'default',
            'terminated': 'danger'
        };
        return <Badge variant={config[status] || 'default'}>{status?.replace('_', ' ')}</Badge>;
    };

    const columns = [
        {
            key: 'contractNumber',
            title: 'Contract Number',
            dataIndex: 'contractNumber',
            render: (num) => <span className="contract-number">{num}</span>
        },
        {
            key: 'title',
            title: 'Title',
            dataIndex: 'title',
            render: (title, row) => (
                <div className="contract-title-cell">
                    <span className="contract-title">{title}</span>
                    <span className="contract-vendor">
                        <Building2 size={12} /> {row.vendor?.name}
                    </span>
                </div>
            )
        },
        {
            key: 'value',
            title: 'Value',
            dataIndex: 'totalValue',
            align: 'right',
            render: (value) => <span className="contract-value">{formatCurrency(value)}</span>
        },
        {
            key: 'period',
            title: 'Period',
            render: (_, row) => (
                <div className="contract-period">
                    <span>{formatDate(row.startDate)}</span>
                    <span className="period-separator">to</span>
                    <span>{formatDate(row.endDate)}</span>
                </div>
            )
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); navigate(`/contracts/${row.id}`); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'draft' && (
                                <DropdownItem icon={Edit2} onClick={() => { close(); navigate(`/contracts/${row.id}/edit`); }}>
                                    Edit
                                </DropdownItem>
                            )}
                            {row.status === 'pending_approval' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionContract(row); setActionType('approve'); }}>
                                    Approve
                                </DropdownItem>
                            )}
                            {row.status === 'approved' && (
                                <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionContract(row); setActionType('activate'); }}>
                                    Activate
                                </DropdownItem>
                            )}
                            {row.status === 'active' && (
                                <DropdownItem icon={XCircle} onClick={() => { close(); setActionContract(row); setActionType('terminate'); }}>
                                    Terminate
                                </DropdownItem>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending_approval', label: 'Pending Approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'active', label: 'Active' },
        { value: 'expired', label: 'Expired' },
        { value: 'terminated', label: 'Terminated' }
    ];

    const tabs = [
        { key: 'all', label: 'All Contracts' },
        { key: 'expiring', label: 'Expiring Soon', icon: AlertTriangle }
    ];

    return (
        <div className="contracts-page">
            <PageHeader
                title="Contract Management"
                subtitle="Manage vendor contracts"
                breadcrumbs={[{ label: 'Contracts', path: '/contracts' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/contracts/create')}>
                        Create Contract
                    </Button>
                }
            />

            <Card>
                <Tabs tabs={tabs} defaultTab="all" onChange={setActiveTab} />

                <div className="contracts-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search contracts..."
                        className="contracts-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="contracts-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={data?.data || []}
                    loading={isLoading}
                    emptyText="No contracts found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="contracts-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionContract}
                onClose={() => { setActionContract(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionContract?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Contract`}
                message={`Are you sure you want to ${actionType} this contract?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={actionType === 'terminate' ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
